import { Button } from '@components/Button'
import { PublicSectionGate } from '@components/PublicPage'
import { Card, Foot, Panel, PanelTitle, SignInPanel, SignInShell } from '@components/SignInCard'
import Spinner from '@components/Spinner'
import { useAuth } from '@context/AuthContext'
import { useWithdrawJoinRequest } from '@hooks/useJoinRequests'
import { useJoinPreview } from '@hooks/useJoinPreview'
import { useMe } from '@hooks/useMe'
import { ProfileNameForm } from '@components/ProfileName'
import { needsProfileName, PROFILE_NAME_TITLE } from '@lib/profileName'
import { useJoinTrip } from '@hooks/useJoinTrip'
import { useTrips } from '@hooks/useTrips'
import { isConflict, isJoinRequestPending } from '@lib/apiError'
import { tripJoinUrl } from '@lib/joinLink'
import { queryKeys } from '@lib/queryKeys'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import InvitationPanel from './InvitationPanel'
import {
	buildJoinInvitation,
	deriveJoinAction,
	deriveMembership,
	joinMethodFromSearch,
	joinRefusal,
	JoinPreview,
	ProfileNameStatus,
} from './core'
import {
	AppLink,
	AttemptError,
	BackButton,
	Checking,
	NameNote,
	PrimaryButton,
	RequestNote,
	SignOutButton,
	StatusLabel,
} from './JoinTripPage.styles'

/**
 * The Join page at `/join/:tripCode` — what a Join link resolves to on the web.
 * Standalone (no app Layout): the login card with the invitation as its hero.
 * A signed-out visitor signs in on the spot; once there is a session the page
 * joins them and lands them on the trip — opening a Join link is the consent,
 * there is no Join button. Someone already in the trip gets "Open trip". On a
 * trip with Join approval on, the same consent sends a Join request instead.
 */
type JoinAttempt =
	| { kind: 'idle' }
	| { kind: 'joining' }
	/** The server answered 409 and the membership re-check could not confirm it. */
	| { kind: 'already-in' }
	/** The trip needs approval: a Join request now waits on the owner. */
	| { kind: 'requested' }
	| { kind: 'failed'; message: string }

const JoinTripPage: React.FC = () => {
	const { tripCode } = useParams<{ tripCode: string }>()
	const location = useLocation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { token, user, isLoading: authLoading, logout } = useAuth()
	const signedIn = !!token

	const preview = useJoinPreview(tripCode)
	const tripsQuery = useTrips({ enabled: signedIn })
	const joinTrip = useJoinTrip()
	const { withdraw, withdrawingCode } = useWithdrawJoinRequest()
	const method = joinMethodFromSearch(location.search)

	const { status: membership, memberTrip } = deriveMembership({
		signedIn,
		isLoading: tripsQuery.isLoading,
		trips: tripsQuery.data?.trips,
		tripCode,
	})
	// A nameless account gives its name before it joins, so the owner never sees a
	// blank member or request. A failed profile lookup doesn't block: the server is the gate.
	const meQuery = useMe({ enabled: signedIn })
	const profileName: ProfileNameStatus = meQuery.isLoading
		? 'loading'
		: needsProfileName(meQuery.data)
			? 'missing'
			: 'set'
	const action = authLoading ? 'checking' : deriveJoinAction({ signedIn, membership, profileName })

	// Signing in clears the whole query cache (a new session must never see the
	// previous account's data), which would blank the invitation into a loading
	// screen for a beat. Keep the last preview on screen while it refills.
	const lastPreview = useRef<JoinPreview | undefined>(undefined)
	if (preview.data) lastPreview.current = preview.data
	const gateQuery =
		preview.isLoading && lastPreview.current
			? { data: lastPreview.current, isLoading: false, error: undefined }
			: preview

	const [attempt, setAttempt] = useState<JoinAttempt>({ kind: 'idle' })

	const join = async (code: string) => {
		setAttempt({ kind: 'joining' })
		try {
			const trip = await joinTrip.mutateAsync({ code, method })
			toast.success('Successfully joined the trip!')
			navigate(`/trips/${trip.id}`)
		} catch (error) {
			if (isConflict(error)) {
				// Already in — they joined from the phone in between. Re-check
				// membership so the page can offer "Open trip" for the right one.
				await queryClient.refetchQueries({ queryKey: queryKeys.trips() })
				setAttempt({ kind: 'already-in' })
				return
			}
			if (isJoinRequestPending(error)) {
				// Not a failure: the request is filed, and it shows in my trips list.
				setAttempt({ kind: 'requested' })
				return
			}
			setAttempt({ kind: 'failed', message: joinRefusal(error) })
		}
	}

	const cancel = (code: string) =>
		withdraw(code, () => {
			toast.success('Request cancelled')
			navigate('/trips')
		})

	// Join the moment the page knows it can: a session, no membership yet, and a
	// valid invitation. Once only — a retry is the person's click, not a re-run.
	const hasInvitation = !!(preview.data ?? lastPreview.current)
	const autoJoinFired = useRef(false)
	useEffect(() => {
		if (action !== 'join' || !hasInvitation || !tripCode || autoJoinFired.current) return
		autoJoinFired.current = true
		void join(tripCode)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [action, hasInvitation, tripCode])

	return (
		<PublicSectionGate
			query={gateQuery}
			loadingLabel="Loading invitation…"
			unavailableTitle="This invitation has expired"
			unavailableMessage="This trip link is no longer valid. Ask the trip organizer to send you a fresh invitation."
			action={
				signedIn ? (
					<Button as={Link} to="/trips" variant="secondary">
						Back to my trips
					</Button>
				) : undefined
			}
		>
			{data => {
				const invitation = buildJoinInvitation(data)
				return (
					<SignInShell>
						<Card>
							<InvitationPanel invitation={invitation} />

							{action === 'sign-in' && (
								<SignInPanel title="Sign in to join" showAppFoot={false}>
									<AppLink href={tripJoinUrl(data.tripInfo.code)}>
										Have the app? <b>Open it there</b>
									</AppLink>
								</SignInPanel>
							)}

							{action === 'checking' && (
								<Panel>
									<Checking>
										<Spinner size={36} />
									</Checking>
								</Panel>
							)}

							{action === 'name' && (
								<Panel>
									<PanelTitle>{PROFILE_NAME_TITLE}</PanelTitle>
									<NameNote>{invitation.inviterName} and the group will see this name.</NameNote>
									<ProfileNameForm
										idPrefix="join-name"
										initialFirstName={meQuery.data?.firstName ?? ''}
										initialLastName={meQuery.data?.lastName ?? ''}
										submitSize="lg"
									/>
								</Panel>
							)}

							{action === 'join' && (attempt.kind === 'idle' || attempt.kind === 'joining') && (
								<Panel>
									<Checking>
										<Spinner size={36} />
										<StatusLabel>Joining {data.tripInfo.name}…</StatusLabel>
									</Checking>
								</Panel>
							)}

							{action === 'join' && attempt.kind === 'failed' && (
								<Panel>
									<PanelTitle>Couldn&rsquo;t join this trip</PanelTitle>
									<AttemptError role="alert">{attempt.message}</AttemptError>
									<PrimaryButton type="button" onClick={() => tripCode && join(tripCode)}>
										Try again
									</PrimaryButton>
									<BackButton type="button" onClick={() => navigate('/trips')}>
										Back to my trips
									</BackButton>
									<Foot>
										Signed in as {user?.firstName || user?.email}. Not you?{' '}
										<SignOutButton type="button" onClick={logout}>
											Sign out
										</SignOutButton>
									</Foot>
								</Panel>
							)}

							{action === 'join' && attempt.kind === 'already-in' && (
								<Panel>
									<PanelTitle>You are already in this trip</PanelTitle>
									<PrimaryButton type="button" onClick={() => navigate('/trips')}>
										Go to my trips
									</PrimaryButton>
								</Panel>
							)}

							{action === 'join' && attempt.kind === 'requested' && (
								<Panel>
									<PanelTitle>Request sent</PanelTitle>
									<RequestNote>
										{invitation.requestSentDetail}.
									</RequestNote>
									<PrimaryButton type="button" onClick={() => navigate('/trips')}>
										Back to my trips
									</PrimaryButton>
									<BackButton
										type="button"
										disabled={withdrawingCode !== null}
										onClick={() => cancel(data.tripInfo.code)}
									>
										Cancel request
									</BackButton>
								</Panel>
							)}

							{action === 'open' && memberTrip && (
								<Panel>
									<PanelTitle>You are already in this trip</PanelTitle>
									<PrimaryButton type="button" onClick={() => navigate(`/trips/${memberTrip.id}`)}>
										Open trip
									</PrimaryButton>
								</Panel>
							)}
						</Card>
					</SignInShell>
				)
			}}
		</PublicSectionGate>
	)
}

export default JoinTripPage
